import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'
import type { IFeaturesGroupToEdit } from '~/types/iap'

import Button from '~/components/Buttons/Button'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import ModalDialog from '~/components/ModalDialog'
import { FORM_ERRORS } from '~/const'
import {
  iapKeys,
  useCreateFeaturesGroupMutation,
  useDeleteFeaturesGroupMutation,
  useUpdateFeaturesGroupMutation,
} from '~/queries/iap'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const MODAL_ID = 'manage-features-group-modal'

interface FormValues {
  featureIds: string[]
  name: string
}

const validationSchema = Yup.object().shape({
  featureIds: Yup.array().of(Yup.string().required()).required(),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  featureGroupToEdit?: IFeaturesGroupToEdit
  featuresOptions?: IOption[]
  isOpen: boolean
  onClose: VoidFunction
  params: {
    appKey?: string
    deviceType?: DeviceType
    featureId?: string
    searchQuery?: string
  }
}

const CreateEditFeaturesGroupModal = ({
  featureGroupToEdit,
  featuresOptions,
  isOpen,
  onClose,
  params,
}: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { appKey, deviceType } = params

  const defaultValues: FormValues = useMemo(
    () => ({
      featureIds: featureGroupToEdit?.featureIds ?? [],
      name: featureGroupToEdit?.name ?? '',
    }),
    [featureGroupToEdit]
  )

  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  /** Queries & Mutations */

  const { mutateAsync: createFeaturesGroup } = useCreateFeaturesGroupMutation()

  const { mutateAsync: updateFeaturesGroup } = useUpdateFeaturesGroupMutation()

  const { mutateAsync: deleteFeaturesGroup } = useDeleteFeaturesGroupMutation()

  /** Handlers */

  const handleModalState = (isOpen: boolean) => () => setIsConfirmModalOpen(isOpen)

  const handleFormSubmit = async ({ featureIds, name }: FormValues) => {
    if (appKey && deviceType) {
      try {
        featureGroupToEdit
          ? await updateFeaturesGroup({
              appKey,
              deviceType,
              featureGroupId: featureGroupToEdit.featureGroupId,
              featureIds,
              name,
            })
          : await createFeaturesGroup({
              appKey,
              deviceType,
              featureIds,
              name,
            })
        await queryClient.invalidateQueries({ queryKey: iapKeys.featuresGroupList(params) })
        onClose()
        displayToast(`Features Group successfully ${featureGroupToEdit ? 'edited' : 'created'}`, 'success')
      } catch (e) {
        captureException(e)
        displayToast(`Features Group could not be ${featureGroupToEdit ? 'edited' : 'created'}`, 'error')
      }
    }
  }

  const handleDeleteConfirm = async () => {
    if (appKey && deviceType && featureGroupToEdit) {
      try {
        await deleteFeaturesGroup({
          featureGroupId: featureGroupToEdit.featureGroupId,
        })
        await queryClient.invalidateQueries({ queryKey: iapKeys.featuresGroupList(params) })
        setIsConfirmModalOpen(false)
        onClose()
        displayToast('Features Group successfully deleted', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Features Group could not be deleted', 'error')
      }
    }
  }

  /** Effects */

  useEffect(() => {
    reset({ featureIds: featureGroupToEdit?.featureIds ?? [], name: featureGroupToEdit?.name ?? '' })
  }, [featureGroupToEdit, reset])

  /** Render */

  return (
    <>
      <Modal className="create-edit-features-group-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
        <ModalHeader>
          <span>{featureGroupToEdit ? 'Edit' : 'Create'} Features Group</span>
        </ModalHeader>
        <form className="create-edit-features-group-form" onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <TextInput
              error={errors.name?.message}
              label="Group name"
              placeholder="Enter a group name"
              required
              {...register('name')}
            />
            <Controller
              control={control}
              name="featureIds"
              render={({ field: { onChange } }) => (
                <MultiSelectInput
                  {...reactSelectMenuPositionDef(MODAL_ID)}
                  className="features-multi-select"
                  label="Features"
                  onChange={(options: unknown) => {
                    const result: string[] = []
                    const castedOptions = options as IOption[]

                    castedOptions.map(({ value }: { value: string }) => result.push(value))
                    onChange(result)
                  }}
                  options={featuresOptions || []}
                  value={featuresOptions?.filter((option) => watch('featureIds').includes(option.value))}
                />
              )}
            />
          </ModalBody>
          <ModalFooter>
            {featureGroupToEdit ? (
              <Button className="modal-component__footer__delete-button" onClick={handleModalState(true)}>
                Delete
              </Button>
            ) : (
              <div />
            )}
            <div className="modal-component__footer__confirm-actions">
              <Button ghost onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={!isDirty || isSubmitting} onClick={handleSubmit(handleFormSubmit)} type="submit">
                {featureGroupToEdit ? 'Save' : 'Create'}
              </Button>
            </div>
          </ModalFooter>
        </form>
      </Modal>
      <ModalDialog
        className="reset-modal"
        isOpen={isConfirmModalOpen}
        mainText="Are you sure you want to delete this Features Group ?"
        onClose={handleModalState(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

export default CreateEditFeaturesGroupModal
